// ─────────────────────────────────────────────────────────────────────────────
// BackToTopButton.tsx — Mobile-only "scroll to top" affordance
//
// Best-practice behaviour:
//   - Hidden until the user has scrolled past one viewport-ish height, so it
//     never appears on short pages or before there's anywhere useful to jump
//     back to (avoids visual clutter near the top of the page).
//   - Sits clear of the fixed MobileTabBar (bottom-20 ≈ tab bar's ~64px+
//     safe-area height) and is hidden entirely while a sheet is open, so it
//     never overlaps the site's own navigation.
//   - Small, semi-transparent, fades/scales in rather than popping in.
//   - Respects prefers-reduced-motion for the scroll itself.
//   - Desktop never shows this — desktop pages are short enough that a
//     scroll-to-top button isn't a established convention there.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUp } from 'lucide-react';
import { useAppSelector } from '@/hooks';

const SHOW_AFTER_PX = 600;

export function BackToTopButton() {
  const [visible, setVisible] = useState(false);
  const mobileSheet = useAppSelector((s) => s.ui.mobileSheet);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > SHOW_AFTER_PX);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToTop = () => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
  };

  return (
    <AnimatePresence>
      {visible && mobileSheet === null && (
        <motion.button
          key="back-to-top"
          onClick={scrollToTop}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ duration: 0.2 }}
          className="md:hidden fixed right-4 z-30 flex items-center justify-center
                     w-11 h-11 rounded-full shadow-float border border-[var(--color-border)]"
          style={{
            bottom: 'calc(72px + max(env(safe-area-inset-bottom), 10px))',
            backgroundColor: 'color-mix(in srgb, var(--color-surface) 90%, transparent)',
            color: 'var(--color-accent)',
          }}
          aria-label="Scroll back to top"
        >
          <ArrowUp size={20} />
        </motion.button>
      )}
    </AnimatePresence>
  );
}
